import { Badge } from "../../../components/tailwind/badge";
import { MessageSquare } from "lucide-react";
import { motion } from "framer-motion";
import { cn } from "../../../lib/utils";

const LIVE_BADGE_CLASS =
  "bg-[hsl(158,48%,38%)]/20 text-[hsl(158,45%,55%)] border-[hsl(158,48%,38%)]/30 hover:bg-[hsl(158,48%,38%)]/20";

const LIVE_DOT_CLASS = "bg-[hsl(158,45%,55%)]";

const getQualityDisplay = (qualityRating) => {
    const qualityMap = {
        'GREEN': 'High',
        'YELLOW': 'Medium',
        'RED': 'Low',
        'UNKNOWN': 'Pending'
    };
    return qualityMap[qualityRating] || 'Pending';
};

const getTierDisplay = (currentLimit) => {
    const tierMap = {
        'TIER_50': 'Tier 1 (50/day)',
        'TIER_250': 'Tier 1 (250/day)',
        'TIER_1K': 'Tier 1 (1K/day)',
        'TIER_10K': 'Tier 2 (10K/day)',
        'TIER_100K': 'Tier 3 (100K/day)',
        'UNLIMITED': 'Unlimited'
    };
    return tierMap[currentLimit] || currentLimit;
};

const qualityDotClass = (qualityRating) => {
    switch (qualityRating) {
        case 'GREEN':
            return 'bg-[hsl(158,48%,38%)]';
        case 'YELLOW':
            return 'bg-yellow-500';
        case 'RED':
            return 'bg-red-500';
        default:
            return 'bg-gray-400';
    }
};

export const WABABanner = ({ isConnected, currentNumber }) => {
    if (isConnected) {
        return (
            <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-gradient-to-r from-[hsl(217,33%,17%)] to-[hsl(217,33%,25%)] dark:from-slate-900 dark:to-slate-800 rounded-2xl p-5 sm:p-6 text-white border border-transparent dark:border-slate-700/80 shadow-sm"
            >
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
                    <div className="flex items-center gap-4 min-w-0">
                        <div className="h-14 w-14 shrink-0 bg-[hsl(158,48%,38%)] rounded-xl flex items-center justify-center">
                            <MessageSquare className="h-7 w-7 text-white" />
                        </div>
                        <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-1">
                                <h2 className="text-lg sm:text-xl font-semibold">WhatsApp Business API</h2>
                                <Badge className={cn(LIVE_BADGE_CLASS, "border")}>
                                    <div className={cn("h-1.5 w-1.5 rounded-full mr-1.5 animate-pulse", LIVE_DOT_CLASS)} />
                                    LIVE
                                </Badge>
                            </div>
                            <p className="text-gray-400 dark:text-slate-400 text-sm truncate">
                                {currentNumber?.displayPhoneNumber} • {currentNumber?.displayName}
                            </p>
                        </div>
                    </div>

                    <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-8 text-xs sm:text-sm w-full sm:w-auto">
                        <div className="flex-1 sm:flex-none">
                            <p className="text-gray-400 dark:text-slate-400 mb-1">Quality Rating</p>
                            <div className="flex items-center gap-2">
                                <div className={cn("h-2.5 w-2.5 rounded-full", qualityDotClass(currentNumber?.qualityRating))} />
                                <span className="font-medium text-white">{getQualityDisplay(currentNumber?.qualityRating)}</span>
                            </div>
                        </div>
                        <div className="hidden sm:block h-8 w-px bg-white/10" />
                        <div className="flex-1 sm:flex-none">
                            <p className="text-gray-400 dark:text-slate-400 mb-1">Messaging Tier</p>
                            <span className="font-medium text-white">{getTierDisplay(currentNumber?.currentLimit)}</span>
                        </div>
                    </div>
                </div>
            </motion.div>
        );
    }

    return (
        <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gradient-to-r from-[hsl(217,33%,17%)] to-[hsl(217,33%,25%)] dark:from-slate-900 dark:to-slate-800 rounded-2xl p-5 sm:p-6 text-white border border-transparent dark:border-slate-700/80 shadow-sm"
        >
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
                <div className="flex items-center gap-4">
                    <div className="h-14 w-14 bg-slate-600 dark:bg-slate-700 rounded-xl flex items-center justify-center">
                        <MessageSquare className="h-7 w-7 text-gray-400" />
                    </div>
                    <div>
                        <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-1">
                            <h2 className="text-lg sm:text-xl font-semibold">WhatsApp Business API</h2>
                            <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">
                                PENDING
                            </Badge>
                        </div>
                        <p className="text-gray-400 dark:text-slate-400 text-sm">No number connected • Connect to get started</p>
                    </div>
                </div>

                <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-8 text-xs sm:text-sm">
                    <div className="flex-1 sm:flex-none">
                        <p className="text-gray-400 dark:text-slate-400 mb-1">Quality Rating</p>
                        <span className="font-medium text-gray-500">--</span>
                    </div>
                    <div className="hidden sm:block h-8 w-px bg-white/10" />
                    <div className="flex-1 sm:flex-none">
                        <p className="text-gray-400 dark:text-slate-400 mb-1">Messaging Tier</p>
                        <span className="font-medium text-gray-500">--</span>
                    </div>
                </div>
            </div>
        </motion.div>
    );
};
